import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const PricingPage = () => {
    const navigate = useNavigate();

    const tiers = [
        {
            name: "Free",
            price: "$0",
            period: "/forever",
            description: "Perfect for getting started",
            features: [
                "10 stocks in watchlist",
                "20 paper trades per month",
                "1 year price history",
                "Basic portfolio analytics",
                "Community access"
            ],
            cta: "Get Started Free",
            highlighted: false
        },
        {
            name: "Pro",
            price: "$9.99",
            period: "/month",
            description: "For serious paper traders",
            features: [
                "100 stocks in watchlist",
                "Unlimited paper trades",
                "Full price history (5+ years)",
                "Advanced analytics & risk metrics",
                "Priority support",
                "Zero ads"
            ],
            cta: "Upgrade to Pro",
            highlighted: true
        }
    ];

    return (
        <div className="min-h-screen bg-[#F9F9F7]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-8">
                            <Link to="/features" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Features</Link>
                            <Link to="/pricing" className="text-sm font-medium text-[#1A4D2E] transition-colors">Pricing</Link>
                            <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">About</Link>
                            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Contact</Link>
                        </nav>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="text-[#1A4D2E] hover:bg-green-50"
                            >
                                Log In
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                className="bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20"
                            >
                                Sign Up
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-32 pb-20 px-6 md:px-12 bg-white">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <h1 className="font-serif text-4xl md:text-6xl text-[#1F2937] leading-tight">
                        Simple, transparent <span className="text-[#1A4D2E]">pricing</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        Start free and upgrade when you need more power. No hidden fees, cancel anytime.
                    </p>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="py-20 px-6 md:px-12">
                <div className="max-w-5xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-8 items-start">
                        {tiers.map((tier, index) => (
                            <Card
                                key={index}
                                className={`relative overflow-hidden ${tier.highlighted
                                        ? 'border-[#1A4D2E] border-2 shadow-xl scale-105 z-10'
                                        : 'border-stone-200 shadow-sm mt-4'
                                    }`}
                            >
                                {tier.highlighted && (
                                    <div className="absolute top-0 right-0 bg-[#C1F23D] text-[#1A4D2E] text-xs font-bold px-3 py-1 rounded-bl-lg">
                                        POPULAR
                                    </div>
                                )}
                                <CardContent className="p-8">
                                    <h3 className="font-serif text-2xl font-semibold text-[#1F2937] mb-2">{tier.name}</h3>
                                    <p className="text-gray-600 mb-6">{tier.description}</p>
                                    <div className="mb-6">
                                        <span className="text-4xl font-bold text-[#1A4D2E]">{tier.price}</span>
                                        <span className="text-gray-500">{tier.period}</span>
                                    </div>
                                    <ul className="space-y-4 mb-8">
                                        {tier.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <Check className="w-5 h-5 text-[#10B981] shrink-0" />
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        className={`w-full py-6 text-lg ${tier.highlighted
                                                ? 'bg-[#1A4D2E] hover:bg-[#143d24] text-white'
                                                : 'bg-white border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-green-50'
                                            }`}
                                        onClick={() => navigate('/register')}
                                    >
                                        {tier.cta}
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 px-6 md:px-12 bg-white">
                <div className="max-w-3xl mx-auto space-y-12">
                    <h2 className="font-serif text-3xl text-center text-[#1F2937]">Frequently Asked Questions</h2>

                    <div className="space-y-8">
                        <div>
                            <h3 className="font-bold text-lg text-[#1F2937] mb-2">Do I need a credit card for the free plan?</h3>
                            <p className="text-gray-600">No, the free plan is completely free forever. You only need a credit card if you decide to upgrade to Pro.</p>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-[#1F2937] mb-2">Can I cancel my subscription?</h3>
                            <p className="text-gray-600">Yes, you can cancel your Pro subscription at any time from your settings page. You'll keep access until the end of your billing period.</p>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-[#1F2937] mb-2">Is the money real?</h3>
                            <p className="text-gray-600">No, FictoInvest is a paper trading simulator. You are trading with virtual currency to learn without risk.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </div>
                        <div className="flex gap-8">
                            <Link to="/features" className="text-gray-600 hover:text-[#1A4D2E]">Features</Link>
                            <Link to="/pricing" className="text-gray-600 hover:text-[#1A4D2E]">Pricing</Link>
                            <Link to="/about" className="text-gray-600 hover:text-[#1A4D2E]">About</Link>
                            <Link to="/contact" className="text-gray-600 hover:text-[#1A4D2E]">Contact</Link>
                        </div>
                        <p className="text-sm text-gray-500">
                            © {new Date().getFullYear()} FictoInvest. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PricingPage;
